import React, { useState, useCallback } from 'react';
import {
  GlassControlProps,
  CarWindowType,
  CarWindowDto,
} from './GlassControl.types';
import { WindowLabelMobile } from './WindowLabelMobile';
import { SectionSummary } from '../SectionSummary';
import { WindowOverlay } from './WindowOverlay';

import carTopView from './car.png';
import carLeftView from './car-left.png';
import carRightView from './car-right.png';

interface WindowConfig {
  type: CarWindowType;
  label: string;
  position: { top: string; left: string };
  showPointer?: boolean;
  pointerHeight?: number;
  pointerDirection?: 'top' | 'bottom' | 'left' | 'right' | 'bottom-left' | 'bottom-right' | 'top-left' | 'top-right';
}

interface OverlayConfig {
  type: CarWindowType;
  position: { top: string; left: string; width: string; height: string };
}

export function GlassControlMobile({ data, className = '' }: GlassControlProps) {
  const windows = data?.fields || ({} as Record<CarWindowType, CarWindowDto>);
  const expertSummary = data?.expertSummary;

  const [selectedWindow, setSelectedWindow] = useState<CarWindowType | null>(null);
  const handleToggle = useCallback((type: CarWindowType) => {
    setSelectedWindow(prev => prev === type ? null : type);
  }, []);

  const hasData = Object.keys(windows).length > 0;

  if (!hasData) {
    return (
      <div className={className}>
        <div className="p-4 text-center text-gray-500 bg-gray-50 rounded-card">
          Brak danych o szybach w pojeździe
        </div>
      </div>
    );
  }

  // Konfiguracja pozycji etykiet dla widoku mobilnego - OSOBNA MAPA
  const topViewWindowsMobile: WindowConfig[] = [
    {
      type: 'FRONT',
      label: 'Przednia',
      position: { top: '20%', left: '58%' },
      showPointer: false,
    },
    {
      type: 'REAR',
      label: 'Tylna',
      position: { top: '72%', left: '35%' },
      showPointer: false,
    },
  ];

  const rightViewWindowsMobile: WindowConfig[] = [
    {
      type: 'RIGHT_REAR_DOOR',
      label: 'Prawe tylne drzwi',
      position: { top: '0%', left: '30%' },
      showPointer: true,
      pointerDirection: 'bottom',
    },
    {
      type: 'RIGHT_FRONT_DOOR',
      label: 'Prawe przednie drzwi',
      position: { top: '40%', left: '50%' },
      showPointer: true,
      pointerDirection: 'top',
    },
    {
      type: 'RIGHT_TRIANGLE',
      label: 'Prawy tył trójkąt',
      position: { top: '60%', left: '13%' },
      showPointer: true,
      pointerHeight: 75,
      pointerDirection: 'top-left',
    },
  ];

  const leftViewWindowsMobile: WindowConfig[] = [
    {
      type: 'LEFT_FRONT_DOOR',
      label: 'Lewe przednie drzwi',
      position: { top: '62%', left: '33%' },
      showPointer: true,
      pointerHeight: 65,
      pointerDirection: 'top-left',
    },
    {
      type: 'LEFT_REAR_DOOR',
      label: 'Lewe tylne drzwi',
      position: { top: '0%', left: '75%' },
      showPointer: true,
      pointerDirection: 'bottom-right',
    },
    {
      type: 'LEFT_TRIANGLE',
      label: 'Lewy tył trójkąt',
      position: { top: '38%', left: '85%' },
      showPointer: true,
      pointerDirection: 'top-right',
      pointerHeight: 20
    },
  ];

  // Konfiguracja pozycji nakładek SVG na szyby - widok z góry
  const topViewOverlaysMobile: OverlayConfig[] = [
    {
      type: 'FRONT',
      position: { top: '24.3%', left: '35.3%', width: '30%', height: '18%' },
    },
    {
      type: 'REAR',
      position: { top: '70%', left: '38.3%', width: '23%', height: '14.2%' },
    },
  ];

  // Konfiguracja pozycji nakładek SVG - strona prawa
  const rightViewOverlaysMobile: OverlayConfig[] = [
    {
      type: 'RIGHT_REAR_DOOR',
      position: { top: '12.7%', left: '20%', width: '20%', height: '28%' },
    },
    {
      type: 'RIGHT_FRONT_DOOR',
      position: { top: '14.3%', left: '40%', width: '25%', height: '28%' },
    },
    {
      type: 'RIGHT_TRIANGLE',
      position: { top: '15.8%', left: '13.5%', width: '9.7%', height: '22%' },
    },
  ];

  // Konfiguracja pozycji nakładek SVG - strona lewa
  const leftViewOverlaysMobile: OverlayConfig[] = [
    {
      type: 'LEFT_FRONT_DOOR',
      position: { top: '14%', left: '33%', width: '25%', height: '30%' },
    },
    {
      type: 'LEFT_REAR_DOOR',
      position: { top: '12.8%', left: '58%', width: '20%', height: '30%' },
    },
    {
      type: 'LEFT_TRIANGLE',
      position: { top: '15.3%', left: '74.5%', width: '9.8%', height: '24%' },
    },
  ];

  const getOverlayVariant = (windowData: CarWindowDto): 'normal' | 'replaced' | 'absence' => {
    if (windowData.absence) return 'absence';
    return 'normal';
  };

  const renderOverlay = (config: OverlayConfig) => {
    const windowData = windows[config.type];
    if (!windowData) return null;

    return (
      <div
        key={`overlay-${config.type}`}
        className="absolute cursor-pointer"
        style={{
          top: config.position.top,
          left: config.position.left,
          width: config.position.width,
          height: config.position.height,
        }}
        onClick={() => handleToggle(config.type)}
      >
        <WindowOverlay
          type={config.type}
          variant={getOverlayVariant(windowData)}
          style={{ width: '100%', height: '100%' }}
          isHighlighted={selectedWindow === config.type}
        />
      </div>
    );
  };

  return (
    <div className={className}>
      <div className="-mx-4 mb-4 space-y-3">
        {/* Widok z góry - Tył / Przód */}
        <div className="overflow-hidden bg-white border-t border-grayBorder">
          <div className="px-4 py-3">
            <h3 className="text-xs font-normal text-text-primary">Tył / Przód</h3>
          </div>
          <div className="relative px-4 pb-4">
            <div className="flex relative justify-center items-center">
              <img
                src={carTopView}
                alt="Widok samochodu z góry"
                className="object-contain w-full max-h-[400px]"
              />
              {/* Nakładki SVG na szyby */}
              {topViewOverlaysMobile.map(renderOverlay)}
              {/* Etykiety */}
              {topViewWindowsMobile.map(({ type, label, position, showPointer, pointerDirection, pointerHeight }) =>
                windows[type] ? (
                  <WindowLabelMobile
                    key={type}
                    data={windows[type]}
                    label={label}
                    pointerHeight={pointerHeight}
                    position={position}
                    isActive={type === 'FRONT'}
                    showPointer={showPointer}
                    pointerDirection={pointerDirection}
                    isHighlighted={selectedWindow === type}
                    onClick={() => handleToggle(type)}
                  />
                ) : null
              )}
            </div>
          </div>
        </div>

        {/* Strona prawa */}
        <div className="overflow-hidden bg-white border-t border-grayBorder pt-5">
          <div className="px-4 py-3">
            <h3 className="text-xs font-normal text-text-primary">Strona prawa</h3>
          </div>
          <div className="relative">
            {/* Gradient lewy */}
            <div className="absolute left-0 top-0 bottom-0 w-[50px] bg-gradient-to-r from-white to-white/0 z-10 pointer-events-none" />
            {/* Gradient prawy */}
            <div className="absolute right-0 top-0 bottom-0 w-[50px] bg-gradient-to-l from-white to-white/0 z-10 pointer-events-none" />

            <div className="flex relative justify-center items-center px-[40px] pt-[40px] pb-4">
              <img
                src={carRightView}
                alt="Widok samochodu - prawa strona"
                className="object-contain w-[585px] max-w-none ml-[70px]"
              />
              {/* Kontener stałej szerokości dla nakładek SVG i etykiet */}
              <div className="absolute w-[500px] h-full top-0 bottom-0 left-1/2 translate-x-[-42%] z-20">
                {/* Nakładki SVG na szyby */}
                {rightViewOverlaysMobile.map(renderOverlay)}
                {/* Etykiety */}
                {rightViewWindowsMobile.map(({ type, label, position, showPointer, pointerDirection, pointerHeight }) =>
                  windows[type] ? (
                    <WindowLabelMobile
                      key={type}
                      data={windows[type]}
                      label={label}
                      pointerHeight={pointerHeight}
                      position={position}
                      showPointer={showPointer}
                      pointerDirection={pointerDirection}
                      isHighlighted={selectedWindow === type}
                      onClick={() => handleToggle(type)}
                    />
                  ) : null
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Strona lewa */}
        <div className="overflow-hidden bg-white border-t border-grayBorder pt-5">
          <div className="px-4 py-3">
            <h3 className="text-xs font-normal text-text-primary">Strona lewa</h3>
          </div>
          <div className="relative">
            {/* Gradient lewy */}
            <div className="absolute left-0 top-0 bottom-0 w-[50px] bg-gradient-to-r from-white to-white/0 z-10 pointer-events-none" />
            {/* Gradient prawy */}
            <div className="absolute right-0 top-0 bottom-0 w-[50px] bg-gradient-to-l from-white to-white/0 z-10 pointer-events-none" />

            <div className="flex relative justify-center items-center px-[40px] pt-[40px] pb-4">
              <img
                src={carLeftView}
                alt="Widok samochodu - lewa strona"
                className="object-contain w-[585px] max-w-none mr-[70px]"
              />
              {/* Kontener stałej szerokości dla nakładek SVG i etykiet */}
              <div className="absolute w-[500px] h-full top-0 bottom-0 left-1/2 -translate-x-[58%] z-20">
                {/* Nakładki SVG na szyby */}
                {leftViewOverlaysMobile.map(renderOverlay)}
                {/* Etykiety */}
                {leftViewWindowsMobile.map(({ type, label, position, showPointer, pointerDirection, pointerHeight }) =>
                  windows[type] ? (
                    <WindowLabelMobile
                      key={type}
                      data={windows[type]}
                      label={label}
                      position={position}
                      pointerHeight={pointerHeight}
                      showPointer={showPointer}
                      pointerDirection={pointerDirection}
                      isHighlighted={selectedWindow === type}
                      onClick={() => handleToggle(type)}
                    />
                  ) : null
                )}
              </div>
            </div>
          </div>
        </div>

      </div>
      {/* Podsumowanie eksperta */}
      {expertSummary && (
        <SectionSummary
          iconName="car-hood"
          title="Podsumowanie Rzeczoznawcy dotyczące kontroli szyb"
          content={expertSummary || ''}
        />
      )}
    </div>
  );
}

export default GlassControlMobile;
